import IEmailNotification from '../../interfaces/IEmailNotification';

type TStatusTag = IEmailNotification['notification']['statusTag'];

type TStatusCounter =
	| 'nbUserEmailToSend'
	| 'nbUserEmailInProgress'
	| 'nbUserEmailSent'
	| 'nbUserEmailBounced'
	| 'nbUserEmailError'
	| 'nbUserEmailUndefined';

type TDocumentEmailNotificationRow = Record<TStatusCounter, number> & {
	id: number;
	fundName?: string;
	documentName?: string;
	documentReleaseDate?: IEmailNotification['documentReleaseDate'];
	nbUserEmail: number;
};

// 'received' has no column. A Map rather than an object literal: a missing
// statusTag must not be coerced to the 'undefined' tag.
const STATUS_COUNTERS = new Map<TStatusTag, TStatusCounter>([
	['planned', 'nbUserEmailToSend'],
	['pending', 'nbUserEmailInProgress'],
	['sent', 'nbUserEmailSent'],
	['bounced', 'nbUserEmailBounced'],
	['error', 'nbUserEmailError'],
	['undefined', 'nbUserEmailUndefined'],
]);

// One row per (fund, document), in order of first appearance, labelled from
// the first notification of the group. Status counters count notifications
// (a user notified twice counts twice), nbUserEmail counts distinct users;
// notifications without a user are left out of both.
// Not alasql: it compiles queries with `new Function`, which the CSP blocks
// (no 'unsafe-eval').
const summarizeByDocument = (notifications: IEmailNotification[]): TDocumentEmailNotificationRow[] => {
	const groups = new Map<string, { row: TDocumentEmailNotificationRow; userIds: Set<number> }>();

	notifications.forEach((notification) => {
		const key = JSON.stringify([notification.fund?.id ?? null, notification.document?.id ?? null]);
		let group = groups.get(key);
		if (!group) {
			group = {
				row: {
					id: groups.size,
					fundName: notification.fund?.name,
					documentName: notification.document?.name,
					documentReleaseDate: notification.documentReleaseDate,
					nbUserEmail: 0,
					nbUserEmailToSend: 0,
					nbUserEmailInProgress: 0,
					nbUserEmailSent: 0,
					nbUserEmailBounced: 0,
					nbUserEmailError: 0,
					nbUserEmailUndefined: 0,
				},
				userIds: new Set(),
			};
			groups.set(key, group);
		}

		const userId = notification.user?.id;
		if (userId === undefined || userId === null) return;
		group.userIds.add(userId);

		const statusTag = notification.notification?.statusTag;
		const counter = statusTag ? STATUS_COUNTERS.get(statusTag) : undefined;
		if (counter) group.row[counter] += 1;
	});

	return Array.from(groups.values(), ({ row, userIds }) => ({ ...row, nbUserEmail: userIds.size }));
};

const HAdminDocumentEmailNotifications = { summarizeByDocument };

export default HAdminDocumentEmailNotifications;
